// Museum branding/config for the kiosk. Bundled defaults are Destino
// Valparaíso — Museo del Inmigrante; on mount we fetch `GET /api/kiosk/
// museum-config` and overlay whatever it returns (name, logo, theme color).
// If the call fails the kiosk keeps the defaults and still works — branding
// must never block ticket sales.

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ApiClient } from "../clients/api-client";

const DEFAULT_NAME = "Destino Valparaíso";

export type KioskConfig = {
  museumName: string;
  museumTagline: string | null;
  logoUrl: string | null;
  /** Auto-reset delay on the "done" screen, in ms. */
  doneResetMs: number;
  /** True once the config fetch has settled (succeeded or failed). */
  ready: boolean;
};

const DEFAULTS: KioskConfig = {
  museumName: DEFAULT_NAME,
  museumTagline: "Museo del Inmigrante",
  logoUrl: null,
  // Long enough to read the confirmation, take the tickets and scan the QR
  // on screen; 8 s was over before the operator looked up (Crane 1,
  // 2026-09-08).
  doneResetMs: 20_000,
  ready: false,
};

const KioskConfigContext = createContext<KioskConfig | null>(null);

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * @param api - Pass `null` to serve the bundled defaults without fetching.
 *   Used by the unprovisioned-terminal gate in App.tsx: the branding chrome
 *   (BrandMark) needs this context, but with no terminal token the
 *   museum-config call could only 401.
 */
export function KioskConfigProvider({
  api,
  children,
}: {
  api: ApiClient | null;
  children: ReactNode;
}) {
  const [config, setConfig] = useState<KioskConfig>(
    api ? DEFAULTS : { ...DEFAULTS, ready: true }
  );

  useEffect(() => {
    if (!api) {
      return;
    }
    let cancelled = false;
    api
      .museumConfig()
      .then((res) => {
        if (cancelled) {
          return;
        }
        const themeColor = res.themeColor?.trim();
        if (themeColor && HEX_COLOR.test(themeColor)) {
          document.documentElement.style.setProperty(
            "--color-accent",
            themeColor
          );
        }
        const name = res.name?.trim() || DEFAULT_NAME;
        setConfig((prev) => ({
          ...prev,
          museumName: name,
          // The bundled tagline only makes sense for the default venue.
          museumTagline: name === DEFAULT_NAME ? prev.museumTagline : null,
          logoUrl: res.logoUrl,
          ready: true,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setConfig((prev) => ({ ...prev, ready: true }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <KioskConfigContext.Provider value={config}>
      {children}
    </KioskConfigContext.Provider>
  );
}

export function useKioskConfig(): KioskConfig {
  const ctx = useContext(KioskConfigContext);
  if (!ctx) {
    throw new Error("useKioskConfig must be used within <KioskConfigProvider>");
  }
  return ctx;
}
